/**
 * Typed access to `liveColumns.json`, the committed snapshot of the live columns (13-17).
 *
 * `liveColumns.json` is generated by `Harmony-QMS/build/registers/export_columns_v1317.py` from a
 * `verify_schema_v121.py --read` snapshot whose verdict was `SCHEMA VERIFY: OK`. Never edit it by hand: re-run the
 * exporter (13-30 re-runs it before the ship and diffs). It holds schema only, no row data.
 *
 * Choice values are read from here, never retyped. A value a rule or a constant names goes through `liveChoice`,
 * which throws at module load if the value is not a live choice, so a renamed choice fails every test at once.
 */

import LIVE from './liveColumns.json';
import { REGISTERS } from '../registers';
import type { RecordRegister } from '../../types/records';

/** The exported column shape (the exporter's allow-list). */
export interface LiveColumn {
  name: string;
  displayName: string;
  type: 'text' | 'choice' | 'dateTime' | 'number' | 'boolean' | 'personOrGroup' | 'calculated' | 'lookup' | 'other';
  format?: 'dateOnly' | 'dateTime';
  maxLength?: number;
  multiline?: boolean;
  choices?: string[];
  required: boolean;
  readOnly: boolean;
  hidden: boolean;
}

export interface LiveColumns {
  as_at: string;
  source_snapshot_sha256: string;
  lists: Record<string, { columns: LiveColumn[] }>;
}

/** The snapshot as committed. */
export const LIVE_COLUMNS = LIVE as LiveColumns;

/** The five registers in a fixed order. */
export const RECORD_REGISTERS: readonly RecordRegister[] = ['incidents', 'complaints', 'feedback', 'risk', 'ci'];

/** The parent list title of a register. */
export function listTitleOf(register: RecordRegister): string {
  return REGISTERS[register].listTitle;
}

/** The audit sibling list title of a register. */
export function auditListTitleOf(register: RecordRegister): string {
  return REGISTERS[register].auditListTitle;
}

/** One live column by list title, or `undefined` when the list has no such column. */
export function liveColumnIn(
  live: LiveColumns,
  listTitle: string,
  name: string,
): LiveColumn | undefined {
  return live.lists[listTitle]?.columns.find((c) => c.name === name);
}

/** One live column on a register's parent list. */
export function liveColumn(register: RecordRegister, name: string): LiveColumn | undefined {
  return liveColumnIn(LIVE_COLUMNS, listTitleOf(register), name);
}

/** The live choices of a choice column on a register's parent list, in live order. Throws if it is not one. */
export function liveChoices(register: RecordRegister, column: string): readonly string[] {
  const col = liveColumn(register, column);
  if (!col || col.type !== 'choice' || !col.choices) {
    throw new Error(`fieldset: ${listTitleOf(register)}.${column} is not a live choice column`);
  }
  return col.choices;
}

/** The live choices of a choice column on a register's audit sibling, in live order. */
export function liveSiblingChoices(register: RecordRegister, column: string): readonly string[] {
  const col = liveColumnIn(LIVE_COLUMNS, auditListTitleOf(register), column);
  if (!col || col.type !== 'choice' || !col.choices) {
    throw new Error(`fieldset: ${auditListTitleOf(register)}.${column} is not a live choice column`);
  }
  return col.choices;
}

/**
 * The named values, each checked to be a live choice of `register.column`. Used where FIELDSET names a value in a
 * rule (`when HarmKind = Harm or Potential harm`) or a constant (`Status` = `New`).
 */
export function liveChoice(register: RecordRegister, column: string, ...values: string[]): readonly string[] {
  const live = liveChoices(register, column);
  for (const v of values) {
    if (!live.includes(v)) {
      throw new Error(`fieldset: ${JSON.stringify(v)} is not a live choice of ${listTitleOf(register)}.${column}`);
    }
  }
  return values;
}
